import { useState, useEffect } from "react";
import { ArrowLeft, Smartphone, Monitor, Tablet, Share, Download, MoreVertical } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";

const Install = () => {
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop'>('desktop');

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform('ios');
    } else if (/android/.test(userAgent)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }
  }, []);

  const PlatformInstructions = () => {
    switch (platform) {
      case 'ios':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                iOS (iPhone/iPad)
              </CardTitle>
              <CardDescription>Install using Safari browser</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">1</div>
                  <div>
                    <p className="font-medium">Open Safari</p>
                    <p className="text-sm text-muted-foreground">Make sure you're using Safari browser (not Chrome or other browsers)</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">2</div>
                  <div>
                    <p className="font-medium">Navigate to the app</p>
                    <p className="text-sm text-muted-foreground">Visit: <code className="bg-muted px-1 rounded text-xs">tradestoolbelt.com</code></p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">3</div>
                  <div>
                    <p className="font-medium flex items-center gap-1">
                      Tap the Share button <Share className="w-4 h-4" />
                    </p>
                    <p className="text-sm text-muted-foreground">Located at the bottom of your Safari browser</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">4</div>
                  <div>
                    <p className="font-medium">Select "Add to Home Screen"</p>
                    <p className="text-sm text-muted-foreground">Scroll down in the share menu and tap this option</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">5</div>
                  <div>
                    <p className="font-medium">Confirm installation</p>
                    <p className="text-sm text-muted-foreground">Tap "Add" in the top right corner</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'android':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                Android
              </CardTitle>
              <CardDescription>Install using Chrome browser</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">1</div>
                  <div>
                    <p className="font-medium">Open Chrome</p>
                    <p className="text-sm text-muted-foreground">Use Chrome browser for the best installation experience</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">2</div>
                  <div>
                    <p className="font-medium">Navigate to the app</p>
                    <p className="text-sm text-muted-foreground">Visit: <code className="bg-muted px-1 rounded text-xs">tradestoolbelt.com</code></p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">3</div>
                  <div>
                    <p className="font-medium flex items-center gap-1">
                      Tap the menu button <MoreVertical className="w-4 h-4" />
                    </p>
                    <p className="text-sm text-muted-foreground">Three dots in the top right corner of Chrome</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">4</div>
                  <div>
                    <p className="font-medium">Select "Add to Home screen"</p>
                    <p className="text-sm text-muted-foreground">Or look for "Install app" option in the menu</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">5</div>
                  <div>
                    <p className="font-medium">Confirm installation</p>
                    <p className="text-sm text-muted-foreground">Tap "Add" or "Install" to complete</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'desktop':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5" />
                Desktop (Chrome, Edge, Brave)
              </CardTitle>
              <CardDescription>Install as a desktop app</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">1</div>
                  <div>
                    <p className="font-medium">Open your browser</p>
                    <p className="text-sm text-muted-foreground">Works best with Chrome, Edge, or Brave</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">2</div>
                  <div>
                    <p className="font-medium">Navigate to the app</p>
                    <p className="text-sm text-muted-foreground">Visit: <code className="bg-muted px-1 rounded text-xs">tradestoolbelt.com</code></p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">3</div>
                  <div>
                    <p className="font-medium flex items-center gap-1">
                      Look for the install icon <Download className="w-4 h-4" />
                    </p>
                    <p className="text-sm text-muted-foreground">In the address bar (right side) or browser menu</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">4</div>
                  <div>
                    <p className="font-medium">Click "Install"</p>
                    <p className="text-sm text-muted-foreground">Or "Add to Desktop" in the popup dialog</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">5</div>
                  <div>
                    <p className="font-medium">Launch from desktop</p>
                    <p className="text-sm text-muted-foreground">Find the app icon on your desktop or in your applications</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        <Link to="/">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <h1 className="text-xl font-bold text-foreground">Install App</h1>
      </div>
      
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">Add to Home Screen</h2>
          <p className="text-muted-foreground">
            Install Trades Toolbelt as an app for quick access and offline capabilities.
          </p>
        </div>

        <div className="mb-6">
          <div className="flex gap-2 p-1 bg-muted rounded-lg w-fit">
            <Button
              variant={platform === 'ios' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setPlatform('ios')}
              className="text-xs"
            >
              <Smartphone className="w-3 h-3 mr-1" />
              iOS
            </Button>
            <Button
              variant={platform === 'android' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setPlatform('android')}
              className="text-xs"
            >
              <Tablet className="w-3 h-3 mr-1" />
              Android
            </Button>
            <Button
              variant={platform === 'desktop' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setPlatform('desktop')}
              className="text-xs"
            >
              <Monitor className="w-3 h-3 mr-1" />
              Desktop
            </Button>
          </div>
        </div>

        <PlatformInstructions />

        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <h3 className="font-medium mb-2">Benefits of Installing</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Quick access from your home screen</li>
            <li>• Works offline for core calculations</li>
            <li>• Faster loading and better performance</li>
            <li>• Native app-like experience</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Install;